import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminTeacherAttendanceManagement = ({ onNavigate, userInfo = {} }) => {
    const [submitting, setSubmitting] = useState(false);
    // Mobile state (used for responsive content layout)
    const [isMobile, setIsMobile] = useState(false);

    // State for teacher attendance data
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(10);

    // Filter states
    const [filters, setFilters] = useState({
        date: '',
        department: '',
        teacher_id: '',
        teacher_name: '',
        status: '',
        shift: ''
    });

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // Form state for teacher attendance
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        teacher_id: '',
        teacher_name: '',
        status: 'present',
        check_in_time: '',
        check_out_time: '',
        remarks: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [demoMode, setDemoMode] = useState(false);

    // Teacher Lookup Suggestions State
    const [suggestedTeachers, setSuggestedTeachers] = useState([]);
    const [suggestedTeachersLoading, setSuggestedTeachersLoading] = useState(false);
    const [activeSuggestionField, setActiveSuggestionField] = useState(null); // 'teacher_id' or 'teacher_name'

    // Department data (fetched from backend)
    const [departments, setDepartments] = useState([]);
    const [departmentsLoading, setDepartmentsLoading] = useState(false);

    const shifts = [
        { value: 'morning', label: 'Morning' },
        { value: 'afternoon', label: 'Afternoon' },
        { value: 'evening', label: 'Evening' },
        { value: 'full_day', label: 'Full Day' }
    ];

    const attendanceStatuses = [
        { value: 'present', label: 'Present' },
        { value: 'absent', label: 'Absent' },
        { value: 'pending', label: 'Pending' },
        { value: 'half-day', label: 'Half Day' }
    ];

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // API Helper for admin endpoints
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
            throw new Error('Authentication token not found');
        }

        const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            },
            ...options
        });

        if (!response.ok) {
            if (response.status === 401) {
                localStorage.removeItem('token');
                sessionStorage.removeItem('token');
                throw new Error('Session expired');
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response.json();
    };

    // Fetch departments from backend
    const fetchDepartments = async () => {
        try {
            setDepartmentsLoading(true);
            const result = await apiCall('/departments');

            if (result.success) {
                setDepartments(result.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    // Fetch departments on mount
    useEffect(() => {
        fetchDepartments();
    }, []);

    // Fetch teachers for auto-complete suggestions
    const handleTeacherLookup = async (query, fieldName) => {
        if (!query || query.trim().length < 2) {
            setSuggestedTeachers([]);
            setActiveSuggestionField(null);
            return;
        }

        setActiveSuggestionField(fieldName);
        setSuggestedTeachersLoading(true);

        try {
            const response = await apiCall(`/teachers?search=${encodeURIComponent(query.trim())}&limit=10`);
            if (response.success && response.data && response.data.teachers) {
                setSuggestedTeachers(response.data.teachers);
            } else {
                setSuggestedTeachers([]);
            }
        } catch (err) {
            console.error('Error fetching teacher suggestions:', err);
            setSuggestedTeachers([]);
        } finally {
            setSuggestedTeachersLoading(false);
        }
    };

    // Close suggestions on outside click
    useEffect(() => {
        const handleClickOutside = () => {
            setSuggestedTeachers([]);
            setActiveSuggestionField(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    // FIXED: Enhanced time extraction and formatting functions
    const extractTimeFromDateTime = (dateTimeString) => {
        if (!dateTimeString) return null;

        // Handle different datetime formats
        try {
            // If it's already just a time (HH:MM or HH:MM:SS)
            if (dateTimeString.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
                return dateTimeString.substring(0, 5); // Return HH:MM
            }

            // If it's a full datetime string (YYYY-MM-DD HH:MM:SS)
            if (dateTimeString.includes(' ')) {
                const timePart = dateTimeString.split(' ')[1];
                if (timePart) {
                    return timePart.substring(0, 5); // Return HH:MM
                }
            }

            // If it's an ISO datetime string
            const date = new Date(dateTimeString);
            if (!isNaN(date.getTime())) {
                return date.toTimeString().substring(0, 5); // Return HH:MM
            }

            return null;
        } catch (error) {
            console.warn('Error parsing datetime:', dateTimeString, error);
            return null;
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return 'Not set';

        // Extract time if it's a datetime
        const extractedTime = extractTimeFromDateTime(timeString);
        if (!extractedTime) return 'Not set';

        // Format time to 12-hour format with AM/PM
        try {
            const [hours, minutes] = extractedTime.split(':');
            const hour24 = parseInt(hours, 10);
            const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
            const ampm = hour24 >= 12 ? 'PM' : 'AM';
            return `${hour12}:${minutes} ${ampm}`;
        } catch (error) {
            return extractedTime; // Fallback to extracted time
        }
    };

    const calculateTotalHours = (entryTime, exitTime) => {
        if (!entryTime || !exitTime) return null;

        try {
            const entry = extractTimeFromDateTime(entryTime);
            const exit = extractTimeFromDateTime(exitTime);

            if (!entry || !exit) return null;

            const [entryHour, entryMin] = entry.split(':').map(Number);
            const [exitHour, exitMin] = exit.split(':').map(Number);

            const entryTotalMinutes = entryHour * 60 + entryMin;
            const exitTotalMinutes = exitHour * 60 + exitMin;

            let totalMinutes = exitTotalMinutes - entryTotalMinutes;

            // Handle next day scenarios (if exit time is before entry time)
            if (totalMinutes < 0) {
                totalMinutes += 24 * 60; // Add 24 hours
            }

            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            return `${hours}.${Math.round(minutes * 100 / 60)}h`;
        } catch (error) {
            console.warn('Error calculating total hours:', error);
            return null;
        }
    };

    const fetchTeacherAttendanceRecords = async (page = 1, filterParams = {}) => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...Object.fromEntries(
                    Object.entries(filterParams).filter(([_, value]) => value && value.trim())
                )
            });

            const apiUrl = `${API_LINK}/api/admin/teacher-attendance?${params}`;
            console.log('Fetching teacher attendance with URL:', apiUrl);

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });

            console.log('Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Response error:', errorText);

                if (response.status === 401) {
                    setError('Session expired. Please login again.');
                    localStorage.removeItem('token');
                    sessionStorage.removeItem('token');
                    return;
                } else if (response.status === 404) {
                    setError('Teacher attendance API endpoint not found. Please check your backend configuration.');
                    return;
                } else if (response.status === 500) {
                    setError('Server error occurred. Please check the backend logs.');
                    return;
                }

                throw new Error(`Server error: ${response.status} - ${errorText}`);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                setError('Server returned invalid response format. Expected JSON.');
                return;
            }

            const result = await response.json();
            console.log('API Response:', result);

            if (result.success) {
                const attendanceData = result.data?.attendance || [];
                const paginationData = result.data?.pagination || {};

                console.log('Attendance data received:', attendanceData);
                console.log('Pagination data:', paginationData);

                setAttendanceRecords(attendanceData);
                setTotalCount(paginationData.totalCount || 0);
                setTotalPages(paginationData.totalPages || 1);
                setCurrentPage(paginationData.currentPage || page);
                setDemoMode(false);
            } else {
                setError(result.message || 'Failed to fetch teacher attendance records from server');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            if (error.name === 'TypeError' && error.message.includes('fetch')) {
                setError('Cannot connect to backend server. Please ensure:\n1. Backend server is running\n2. Teacher attendance API routes are properly configured\n3. Database connection is working');
            } else if (error.message.includes('JSON')) {
                setError('Server returned invalid data format. Please check backend API.');
            } else {
                setError(`Failed to load teacher attendance records: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Demo data for testing when API is not available
    const loadDemoData = (page = 1, filterParams = {}) => {
        console.log('Loading demo teacher attendance data...');
        setDemoMode(true);

        // Create demo teacher attendance records with proper datetime format
        const demoData = [
            {
                id: 1,
                attendance_date: '2024-07-30',
                teacher_id: 'T001',
                teacher_name: 'Dr. Rajesh Kumar',
                department: 'Computer Science and Engineering',
                status: 'present',
                shift: 'morning',
                entry_time: '2024-07-30 09:00:00',
                exit_time: '2024-07-30 17:00:00',
                total_hours: 8.0,
                remarks: 'Regular duty'
            },
            {
                id: 2,
                attendance_date: '2024-07-30',
                teacher_id: 'T002',
                teacher_name: 'Prof. Priya Sharma',
                department: 'Electrical Engineering',
                status: 'present',
                shift: 'morning',
                entry_time: '2024-07-30 09:15:00',
                exit_time: '2024-07-30 17:30:00',
                total_hours: 8.25,
                remarks: 'Lab supervision'
            },
            {
                id: 3,
                attendance_date: '2024-07-30',
                teacher_id: 'T003',
                teacher_name: 'Dr. Amit Singh',
                department: 'MECHANICAL ENGINEERING',
                status: 'present',
                shift: 'morning',
                entry_time: '2024-07-30 09:45:00',
                exit_time: '2024-07-30 17:00:00',
                total_hours: 7.25,
                remarks: 'Traffic delay'
            },
            {
                id: 4,
                attendance_date: '2024-07-30',
                teacher_id: 'T004',
                teacher_name: 'Ms. Neha Verma',
                department: 'Biotechnology',
                status: 'present',
                shift: 'afternoon',
                entry_time: '2024-07-30 13:00:00',
                exit_time: '2024-07-30 21:00:00',
                total_hours: 8.0,
                remarks: 'Evening classes'
            },
            {
                id: 5,
                attendance_date: '2024-07-30',
                teacher_id: 'T005',
                teacher_name: 'Prof. Vikram Chandra',
                department: 'Civil Engineering',
                status: 'half-day',
                shift: 'morning',
                entry_time: '2024-07-30 09:00:00',
                exit_time: '2024-07-30 13:00:00',
                total_hours: 4.0,
                remarks: 'Medical appointment'
            },
            {
                id: 6,
                attendance_date: '2024-07-29',
                teacher_id: 'T001',
                teacher_name: 'Dr. Rajesh Kumar',
                department: 'Computer Science and Engineering',
                status: 'present',
                shift: 'morning',
                entry_time: '2024-07-29 08:55:00',
                exit_time: '2024-07-29 17:05:00',
                total_hours: 8.17,
                remarks: 'Faculty meeting'
            },
            {
                id: 7,
                attendance_date: '2024-07-29',
                teacher_id: 'T006',
                teacher_name: 'Dr. Sunita Rao',
                department: 'Microbiology',
                status: 'absent',
                shift: 'morning',
                entry_time: null,
                exit_time: null,
                total_hours: 0,
                remarks: 'Medical leave'
            },
            {
                id: 8,
                attendance_date: '2024-07-29',
                teacher_id: 'T007',
                teacher_name: 'Prof. Manoj Gupta',
                department: 'Department of Business Management',
                status: 'present',
                shift: 'evening',
                entry_time: '2024-07-29 15:00:00',
                exit_time: '2024-07-29 22:00:00',
                total_hours: 7.0,
                remarks: 'MBA classes'
            }
        ];

        // Apply filters
        let filteredData = demoData;

        if (filterParams.date) {
            filteredData = filteredData.filter(record => record.attendance_date === filterParams.date);
        }
        if (filterParams.department) {
            filteredData = filteredData.filter(record =>
                record.department.toLowerCase().includes(filterParams.department.toLowerCase())
            );
        }
        if (filterParams.teacher_id) {
            filteredData = filteredData.filter(record =>
                record.teacher_id.toLowerCase().includes(filterParams.teacher_id.toLowerCase())
            );
        }
        if (filterParams.teacher_name) {
            filteredData = filteredData.filter(record =>
                record.teacher_name.toLowerCase().includes(filterParams.teacher_name.toLowerCase())
            );
        }
        if (filterParams.status) {
            filteredData = filteredData.filter(record => record.status === filterParams.status);
        }
        if (filterParams.shift) {
            filteredData = filteredData.filter(record => record.shift === filterParams.shift);
        }

        // Apply pagination
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        // Update state
        setAttendanceRecords(paginatedData);
        setTotalCount(filteredData.length);
        setTotalPages(Math.ceil(filteredData.length / limit));
        setCurrentPage(page);

        if (filteredData.length > 0) {
            console.log(`Loaded ${paginatedData.length} demo teacher attendance records`);
        }
    };

    // Initial data fetch
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchTeacherAttendanceRecords(1, filters);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    // Handle filter changes
    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...filters, [filterName]: value };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchTeacherAttendanceRecords(1, newFilters);
    };

    // Clear all filters
    const clearFilters = () => {
        const clearedFilters = {
            date: '',
            department: '',
            teacher_id: '',
            teacher_name: '',
            status: '',
            shift: ''
        };
        setFilters(clearedFilters);
        setCurrentPage(1);
        fetchTeacherAttendanceRecords(1, clearedFilters);
    };

    // Handle pagination
    const handlePageChange = (page) => {
        if (page && page !== currentPage && page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchTeacherAttendanceRecords(page, filters);
        }
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.date.trim()) errors.date = 'Date is required';
        if (!formData.teacher_id.trim()) errors.teacher_id = 'Teacher ID is required';
        if (!formData.teacher_name.trim()) errors.teacher_name = 'Teacher name is required';
        if (!formData.status.trim()) errors.status = 'Status is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission for add/edit teacher attendance
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const url = selectedRecord
                ? `${API_LINK}/api/admin/teacher-attendance/${selectedRecord.id}`
                : `${API_LINK}/api/admin/teacher-attendance`;

            const method = selectedRecord ? 'PUT' : 'POST';

            // Prepare data according to svu_attendence_teacher_attendance table structure
            const attendanceData = {
                teacher_id: formData.teacher_id,
                teacher_name: formData.teacher_name,
                attendance_date: formData.date,
                entry_time: formData.check_in_time || null,
                exit_time: formData.check_out_time || null,
                status: formData.status,
                location_verified: 1,
                remarks: formData.remarks || null
            };

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(attendanceData)
            });

            if (!response.ok) {
                if (response.status === 404) {
                    setError('Teacher attendance API endpoint not implemented. Backend needs to create: POST/PUT /api/admin/teacher-attendance');
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setShowAddModal(false);
                setShowEditModal(false);
                resetForm();
                fetchTeacherAttendanceRecords(currentPage || 1, filters);
                alert(selectedRecord ? 'Teacher attendance record updated successfully!' : 'Teacher attendance record added successfully!');
            } else {
                alert(result.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Submit error:', error);
            setError(`Failed to save teacher attendance record: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete teacher attendance record
    const handleDelete = async () => {
        if (!selectedRecord) return;

        try {
            setSubmitting(true);

            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${API_LINK}/api/admin/teacher-attendance/${selectedRecord.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    setError('Teacher attendance delete API endpoint not implemented. Backend needs to create: DELETE /api/admin/teacher-attendance/:id');
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setSelectedRecord(null);
                fetchTeacherAttendanceRecords(currentPage || 1, filters);
                alert('Teacher attendance record deleted successfully!');
            } else {
                alert(result.message || 'Delete failed');
            }
        } catch (error) {
            console.error('Delete error:', error);
            setError(`Failed to delete teacher attendance record: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            teacher_id: '',
            teacher_name: '',
            status: 'present',
            check_in_time: '',
            check_out_time: '',
            remarks: ''
        });
        setFormErrors({});
        setSelectedRecord(null);
        setSuggestedTeachers([]);
        setActiveSuggestionField(null);
    };

    // Handle edit click
    const handleEditClick = (record) => {
        setSelectedRecord(record);
        
        // Parse date safely to YYYY-MM-DD for HTML5 date input compatibility
        let formattedDate = '';
        if (record.attendance_date) {
            try {
                if (/^\d{4}-\d{2}-\d{2}$/.test(record.attendance_date)) {
                    formattedDate = record.attendance_date;
                } else {
                    const d = new Date(record.attendance_date);
                    if (!isNaN(d.getTime())) {
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        formattedDate = `${year}-${month}-${day}`;
                    } else {
                        formattedDate = record.attendance_date;
                    }
                }
            } catch (e) {
                formattedDate = record.attendance_date || '';
            }
        }
        if (!formattedDate) {
            formattedDate = new Date().toISOString().split('T')[0];
        }

        setFormData({
            date: formattedDate,
            teacher_id: record.teacher_id || '',
            teacher_name: record.teacher_name || '',
            department: record.department || '',
            status: record.status || 'present',
            shift: record.shift || 'morning',
            check_in_time: extractTimeFromDateTime(record.entry_time) || '',
            check_out_time: extractTimeFromDateTime(record.exit_time) || '',
            remarks: record.remarks || ''
        });
        setShowEditModal(true);
    };

    // Handle delete click
    const handleDeleteClick = (record) => {
        setSelectedRecord(record);
        setShowDeleteModal(true);
    };



    const formatDate = (dateString) => {
        if (!dateString || dateString === '0000-00-00') return 'Not set';
        try {
            // If it's in YYYY-MM-DD format, format directly to DD/MM/YYYY to avoid timezone shift
            if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
                const [year, month, day] = dateString.split('-');
                return `${day}/${month}/${year}`;
            }

            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            
            return `${day}/${month}/${year}`;
        } catch (e) {
            return dateString;
        }
    };

    // Get status badge color based on database enum values
    const getStatusColor = (status) => {
        switch (status) {
            case 'present': return { bg: '#e8f5e9', color: '#2e7d32' };
            case 'absent': return { bg: '#ffebee', color: '#d32f2f' };
            case 'pending': return { bg: '#fff3e0', color: '#f57c00' };
            case 'half-day': return { bg: '#e3f2fd', color: '#1976d2' };
            default: return { bg: '#f5f5f5', color: '#666' };
        }
    };

    return (
        <Layout role="admin" onNavigate={onNavigate} userInfo={userInfo} activePage="admin-teacher-attendance">
            <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
            </style>
            {/* Welcome Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    Teacher Attendance Management 👨‍🏫
                </h2>
                <p style={{ margin: 0, opacity: 0.9 }}>
                    Manage faculty attendance records, track daily attendance, and maintain comprehensive teacher attendance database.
                </p>
            </div>

            {/* Filters and Controls Section */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                {/* Filter Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem'
                }}>
                    {/* Date Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Date
                        </label>
                        <input
                            type="date"
                            value={filters.date}
                            onChange={(e) => handleFilterChange('date', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#238548';
                                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </div>

                    {/* Department Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Department
                        </label>
                        <select
                            value={filters.department}
                            onChange={(e) => handleFilterChange('department', e.target.value)}
                            disabled={departmentsLoading}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                backgroundColor: departmentsLoading ? '#f9fafb' : '#ffffff',
                                cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#238548';
                                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <option value="">
                                {departmentsLoading ? 'Loading Departments...' : 'All Departments'}
                            </option>
                            {departments.map(dept => (
                                <option key={dept.value} value={dept.value}>{dept.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Teacher ID Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Teacher ID
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Teacher ID..."
                            value={filters.teacher_id}
                            onChange={(e) => handleFilterChange('teacher_id', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#238548';
                                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </div>

                    {/* Teacher Name Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Teacher Name
                        </label>
                        <input
                            type="text"
                            placeholder="Enter Teacher Name..."
                            value={filters.teacher_name}
                            onChange={(e) => handleFilterChange('teacher_name', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#238548';
                                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        />
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Status
                        </label>
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#238548';
                                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <option value="">All Status</option>
                            {attendanceStatuses.map(status => (
                                <option key={status.value} value={status.value}>{status.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Shift Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '0.5rem'
                        }}>
                            Shift
                        </label>
                        <select
                            value={filters.shift}
                            onChange={(e) => handleFilterChange('shift', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#238548';
                                e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.boxShadow = 'none';
                            }}
                        >
                            <option value="">All Shifts</option>
                            {shifts.map(shift => (
                                <option key={shift.value} value={shift.value}>{shift.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #ecf0f1'
                }}>
                    <button
                        onClick={clearFilters}
                        style={{
                            padding: '0.75rem 1.5rem',
                            border: '2px solid #e5e7eb',
                            backgroundColor: '#ffffff',
                            color: '#374151',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.backgroundColor = '#ffffff';
                        }}
                    >
                        🔄 Clear Filters
                    </button>

                    <div style={{ flex: 1 }}></div>

                    <button
                        onClick={() => {
                            resetForm();
                            setShowAddModal(true);
                        }}
                        style={{
                            padding: '0.75rem 2rem',
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>+</span>
                        Add Teacher Attendance
                    </button>
                </div>

                {/* Stats */}
                <div style={{
                    paddingTop: '1rem',
                    fontSize: '0.9rem',
                    color: '#666'
                }}>
                    {loading ? 'Loading teacher attendance records...' :
                        error ? 'Error loading teacher attendance records' :
                            `Showing ${attendanceRecords.length} of ${totalCount} teacher attendance records`}
                    {Object.values(filters).some(filter => filter) && !loading && !error && ` (filtered)`}
                </div>
            </div>

            {/* Error Message with API Implementation Guide */}
            {error && (
                <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '2rem'
                }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Teacher Attendance API Not Implemented</p>
                    <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
                    <div style={{
                        backgroundColor: '#fef7f7',
                        border: '1px solid #f5c6cb',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        fontSize: '0.9rem'
                    }}>
                        <strong>Required API Implementation:</strong>
                        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
                            <li><strong>GET</strong> /api/admin/teacher-attendance - Fetch records from <code>svu_attendence_teacher_attendance</code></li>
                            <li><strong>POST</strong> /api/admin/teacher-attendance - Add new teacher attendance record</li>
                            <li><strong>PUT</strong> /api/admin/teacher-attendance/:id - Update existing record</li>
                            <li><strong>DELETE</strong> /api/admin/teacher-attendance/:id - Delete record</li>
                        </ul>
                        <strong>Database Table:</strong> <code>svu_attendence_teacher_attendance</code><br />
                        <strong>Key Fields:</strong> teacher_id, teacher_name, attendance_date, entry_time, exit_time, status, total_hours
                    </div>
                    <button
                        onClick={() => fetchTeacherAttendanceRecords(currentPage, filters)}
                        style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Teacher Attendance Records Table */}
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                {loading && attendanceRecords.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '4rem',
                        flexDirection: 'column',
                        gap: '1rem'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #e3f2fd',
                            borderTop: '4px solid #238548',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading teacher attendance records...</p>
                    </div>
                ) : attendanceRecords.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem 2rem'
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>👨‍🏫</div>
                        <h3 style={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: '#333',
                            margin: '0 0 1rem 0'
                        }}>
                            No teacher attendance records found
                        </h3>
                        <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
                            {Object.values(filters).some(filter => filter)
                                ? 'Try adjusting your filters or clearing them to see more records'
                                : 'Get started by adding your first teacher attendance record to the system'
                            }
                        </p>
                        <button
                            onClick={() => {
                                resetForm();
                                setShowAddModal(true);
                            }}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            Add First Record
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: isMobile ? '0.85rem' : '0.95rem'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                        color: '#ffffff'
                                    }}>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Date</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Teacher ID</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Name</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Department</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Shift</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Time</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attendanceRecords.map((record, index) => {
                                        const statusColors = getStatusColor(record.status);
                                        const entryTime = extractTimeFromDateTime(record.entry_time);
                                        const exitTime = extractTimeFromDateTime(record.exit_time);
                                        const totalHours = record.total_hours || calculateTotalHours(record.entry_time, record.exit_time);

                                        return (
                                            <tr key={record.id} style={{
                                                borderBottom: '1px solid #f1f5f9',
                                                transition: 'background-color 0.3s ease'
                                            }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                            >
                                                <td style={{ padding: '1rem', fontWeight: 500, textAlign: 'left' }}>
                                                    {formatDate(record.attendance_date)}
                                                </td>
                                                <td style={{ padding: '1rem', color: '#238548', fontWeight: 500, textAlign: 'left' }}>
                                                    {record.teacher_id}
                                                </td>
                                                <td style={{ padding: '1rem', color: '#333', fontWeight: 500, textAlign: 'left' }}>
                                                    {record.teacher_name}
                                                </td>
                                                <td style={{ padding: '1rem', color: '#333', textAlign: 'left' }}>
                                                    <span style={{
                                                        padding: '0.25rem 0.75rem',
                                                        backgroundColor: '#e8f5e9',
                                                        color: '#2e7d32',
                                                        borderRadius: '20px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {record.department || 'N/A'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '1rem', color: '#333', textAlign: 'left' }}>
                                                    <span style={{
                                                        padding: '0.25rem 0.75rem',
                                                        backgroundColor: '#e3f2fd',
                                                        color: '#1976d2',
                                                        borderRadius: '20px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 500,
                                                        textTransform: 'capitalize'
                                                    }}>
                                                        {record.shift || 'Full Day'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '0.5rem 1rem',
                                                        borderRadius: '20px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        backgroundColor: statusColors.bg,
                                                        color: statusColors.color
                                                    }}>
                                                        {record.status === 'present' && '✓ Present'}
                                                        {record.status === 'absent' && '✗ Absent'}
                                                        {record.status === 'pending' && '⏳ Pending'}
                                                        {record.status === 'half-day' && '◐ Half Day'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '1rem', textAlign: 'center', color: '#333' }}>
                                                    <div style={{ fontSize: '0.8rem' }}>
                                                        {entryTime && (
                                                            <div style={{ marginBottom: '0.25rem' }}>
                                                                <strong>In:</strong> {formatTime(entryTime)}
                                                            </div>
                                                        )}
                                                        {exitTime && (
                                                            <div style={{ marginBottom: '0.25rem' }}>
                                                                <strong>Out:</strong> {formatTime(exitTime)}
                                                            </div>
                                                        )}
                                                        {totalHours && (
                                                            <div style={{ color: '#666', fontSize: '0.7rem' }}>
                                                                <strong>Total:</strong> {typeof totalHours === 'number' ? `${totalHours}h` : totalHours}
                                                            </div>
                                                        )}
                                                        {!entryTime && !exitTime && (
                                                            <div style={{ color: '#999', fontStyle: 'italic' }}>
                                                                Not recorded
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                        <button
                                                            onClick={() => handleEditClick(record)}
                                                            style={{
                                                                padding: '0.5rem 1rem',
                                                                backgroundColor: '#e3f2fd',
                                                                color: '#1976d2',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                fontSize: '0.85rem',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                fontWeight: 500
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.backgroundColor = '#1976d2';
                                                                e.target.style.color = '#ffffff';
                                                                e.target.style.transform = 'translateY(-1px)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.backgroundColor = '#e3f2fd';
                                                                e.target.style.color = '#1976d2';
                                                                e.target.style.transform = 'translateY(0)';
                                                            }}
                                                        >
                                                            ✏️ Edit
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(record)}
                                                            style={{
                                                                padding: '0.5rem 1rem',
                                                                backgroundColor: '#ffebee',
                                                                color: '#d32f2f',
                                                                border: 'none',
                                                                borderRadius: '6px',
                                                                fontSize: '0.85rem',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                fontWeight: 500
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.target.style.backgroundColor = '#d32f2f';
                                                                e.target.style.color = '#ffffff';
                                                                e.target.style.transform = 'translateY(-1px)';
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.target.style.backgroundColor = '#ffebee';
                                                                e.target.style.color = '#d32f2f';
                                                                e.target.style.transform = 'translateY(0)';
                                                            }}
                                                        >
                                                            🗑️ Delete
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={{
                                padding: '1.5rem',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '1rem'
                            }}>
                                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                                    Page {currentPage} of {totalPages} • {totalCount} total records
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                                            color: currentPage === 1 ? '#9ca3af' : '#374151',
                                            borderRadius: '6px',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (currentPage !== 1) {
                                                e.target.style.borderColor = '#238548';
                                                e.target.style.backgroundColor = '#f0fdf4';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (currentPage !== 1) {
                                                e.target.style.borderColor = '#e2e8f0';
                                                e.target.style.backgroundColor = '#ffffff';
                                            }
                                        }}
                                    >
                                        ← Previous
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                                        let page;
                                        if (totalPages <= 5) {
                                            page = index + 1;
                                        } else if (currentPage <= 3) {
                                            page = index + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            page = totalPages - 4 + index;
                                        } else {
                                            page = currentPage - 2 + index;
                                        }

                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                style={{
                                                    padding: '0.5rem 0.75rem',
                                                    border: '2px solid',
                                                    borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                                                    backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                                                    color: page === currentPage ? '#ffffff' : '#374151',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    fontSize: '0.9rem',
                                                    fontWeight: page === currentPage ? '600' : '400'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (page !== currentPage) {
                                                        e.target.style.borderColor = '#238548';
                                                        e.target.style.backgroundColor = '#f0fdf4';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (page !== currentPage) {
                                                        e.target.style.borderColor = '#e2e8f0';
                                                        e.target.style.backgroundColor = '#ffffff';
                                                    }
                                                }}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                                            color: currentPage === totalPages ? '#9ca3af' : '#374151',
                                            borderRadius: '6px',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (currentPage !== totalPages) {
                                                e.target.style.borderColor = '#238548';
                                                e.target.style.backgroundColor = '#f0fdf4';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (currentPage !== totalPages) {
                                                e.target.style.borderColor = '#e2e8f0';
                                                e.target.style.backgroundColor = '#ffffff';
                                            }
                                        }}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>



            {/* Add/Edit Modal */}
            {
                (showAddModal || showEditModal) && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '1rem'
                    }}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                            width: '100%',
                            maxWidth: '800px',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                color: '#ffffff',
                                padding: '1.5rem 2rem',
                                borderRadius: '12px 12px 0 0'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    {selectedRecord ? '✏️ Edit Teacher Attendance' : '➕ Add Teacher Attendance'}
                                </h3>
                                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                                    {selectedRecord ? 'Update teacher attendance information' : 'Enter new teacher attendance details'}
                                </p>
                            </div>

                            {/* Modal Content */}
                            <div style={{
                                padding: '2rem',
                                overflowY: 'auto',
                                flex: 1
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
                                    {/* Date */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.date}
                                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.date ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.date ? '#fef2f2' : '#ffffff'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.date) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.date) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {formErrors.date && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.date}
                                            </p>
                                        )}
                                    </div>

                                    {/* Teacher ID */}
                                    <div style={{ position: 'relative' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Teacher ID *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.teacher_id}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData({ ...formData, teacher_id: val });
                                                handleTeacherLookup(val, 'teacher_id');
                                            }}
                                            placeholder="e.g., T001, TCH-2024-001"
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.teacher_id ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.teacher_id ? '#fef2f2' : '#ffffff'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.teacher_id) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.teacher_id) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {activeSuggestionField === 'teacher_id' && (suggestedTeachers.length > 0 || suggestedTeachersLoading) && (
                                            <div 
                                                onClick={(e) => e.stopPropagation()} 
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                                    zIndex: 2010,
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    marginTop: '0.25rem'
                                                }}
                                            >
                                                {suggestedTeachersLoading ? (
                                                    <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.85rem' }}>🔍 Searching teachers...</div>
                                                ) : (
                                                    suggestedTeachers.map(teacher => (
                                                        <div
                                                            key={teacher.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setFormData({
                                                                    ...formData,
                                                                    teacher_id: teacher.id.trim(),
                                                                    teacher_name: teacher.name.trim()
                                                                });
                                                                setSuggestedTeachers([]);
                                                                setActiveSuggestionField(null);
                                                            }}
                                                            style={{
                                                                padding: '0.75rem',
                                                                cursor: 'pointer',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                transition: 'background-color 0.2s',
                                                                fontSize: '0.85rem',
                                                                textAlign: 'left'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <span style={{ fontWeight: 600, color: '#1f2937' }}>{teacher.id.trim()}</span> - <span style={{ color: '#4b5563' }}>{teacher.name.trim()}</span>
                                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{teacher.department?.trim() || 'No Department'}</div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        {formErrors.teacher_id && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.teacher_id}
                                            </p>
                                        )}
                                    </div>

                                    {/* Teacher Name */}
                                    <div style={{ position: 'relative' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Teacher Name *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.teacher_name}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFormData({ ...formData, teacher_name: val });
                                                handleTeacherLookup(val, 'teacher_name');
                                            }}
                                            placeholder="e.g., Dr. John Smith, Prof. Jane Doe"
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.teacher_name ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.teacher_name ? '#fef2f2' : '#ffffff'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.teacher_name) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.teacher_name) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {activeSuggestionField === 'teacher_name' && (suggestedTeachers.length > 0 || suggestedTeachersLoading) && (
                                            <div 
                                                onClick={(e) => e.stopPropagation()} 
                                                style={{
                                                    position: 'absolute',
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    backgroundColor: '#ffffff',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                                    zIndex: 2010,
                                                    maxHeight: '200px',
                                                    overflowY: 'auto',
                                                    marginTop: '0.25rem'
                                                }}
                                            >
                                                {suggestedTeachersLoading ? (
                                                    <div style={{ padding: '0.75rem', color: '#6b7280', fontSize: '0.85rem' }}>🔍 Searching teachers...</div>
                                                ) : (
                                                    suggestedTeachers.map(teacher => (
                                                        <div
                                                            key={teacher.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setFormData({
                                                                    ...formData,
                                                                    teacher_id: teacher.id.trim(),
                                                                    teacher_name: teacher.name.trim()
                                                                });
                                                                setSuggestedTeachers([]);
                                                                setActiveSuggestionField(null);
                                                            }}
                                                            style={{
                                                                padding: '0.75rem',
                                                                cursor: 'pointer',
                                                                borderBottom: '1px solid #f3f4f6',
                                                                transition: 'background-color 0.2s',
                                                                fontSize: '0.85rem',
                                                                textAlign: 'left'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                        >
                                                            <span style={{ fontWeight: 600, color: '#1f2937' }}>{teacher.id.trim()}</span> - <span style={{ color: '#4b5563' }}>{teacher.name.trim()}</span>
                                                            <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{teacher.department?.trim() || 'No Department'}</div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                        {formErrors.teacher_name && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.teacher_name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Attendance Status */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Attendance Status *
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.status ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.status ? '#fef2f2' : '#ffffff'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.status) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.status) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            {attendanceStatuses.map(status => (
                                                <option key={status.value} value={status.value}>{status.label}</option>
                                            ))}
                                        </select>
                                        {formErrors.status && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.status}
                                            </p>
                                        )}
                                    </div>

                                    {/* Check In Time */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Check In Time
                                        </label>
                                        <input
                                            type="time"
                                            value={formData.check_in_time}
                                            onChange={(e) => setFormData({ ...formData, check_in_time: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                    </div>

                                    {/* Check Out Time */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Check Out Time
                                        </label>
                                        <input
                                            type="time"
                                            value={formData.check_out_time}
                                            onChange={(e) => setFormData({ ...formData, check_out_time: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                    </div>

                                    {/* Remarks */}
                                    <div style={{ gridColumn: '1 / -1' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Remarks
                                        </label>
                                        <textarea
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            placeholder="Optional remarks or notes..."
                                            rows="3"
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                resize: 'vertical',
                                                minHeight: '80px'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '1.5rem 2rem',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem',
                                backgroundColor: '#f8fafc'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setShowEditModal(false);
                                        resetForm();
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        border: '2px solid #e5e7eb',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 500
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.backgroundColor = '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.backgroundColor = '#ffffff';
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    style={{
                                        padding: '0.75rem 2rem',
                                        border: 'none',
                                        background: submitting
                                            ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                            : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <div style={{
                                                width: '16px',
                                                height: '16px',
                                                border: '2px solid #ffffff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            {selectedRecord ? '💾 Update Record' : '➕ Add Record'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && selectedRecord && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '1rem'
                    }}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                            maxWidth: '400px',
                            width: '100%'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: '2rem 2rem 1rem 2rem',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    background: '#fee2e2',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1rem',
                                    fontSize: '2rem'
                                }}>
                                    🗑️
                                </div>
                                <h3 style={{
                                    margin: '0 0 1rem 0',
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    color: '#dc2626'
                                }}>
                                    Delete Teacher Attendance
                                </h3>
                                <p style={{
                                    margin: '0 0 1rem 0',
                                    color: '#6b7280',
                                    lineHeight: 1.6
                                }}>
                                    Are you sure you want to delete the attendance record for <strong style={{ color: '#374151' }}>{selectedRecord.teacher_name}</strong> ({selectedRecord.teacher_id}) on <strong style={{ color: '#374151' }}>{formatDate(selectedRecord.attendance_date)}</strong>?
                                </p>
                                <p style={{
                                    margin: 0,
                                    color: '#dc2626',
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                }}>
                                    This action cannot be undone.
                                </p>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '1rem 2rem 2rem 2rem',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '1rem'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedRecord(null);
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        border: '2px solid #e5e7eb',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 500
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.backgroundColor = '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.backgroundColor = '#ffffff';
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={submitting}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        border: 'none',
                                        background: submitting
                                            ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                            : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <div style={{
                                                width: '16px',
                                                height: '16px',
                                                border: '2px solid #ffffff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            Deleting...
                                        </>
                                    ) : (
                                        '🗑️ Delete Record'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Demo Mode Notice */}
            {
                demoMode && (
                    <div style={{
                        position: 'fixed',
                        bottom: '2rem',
                        right: '2rem',
                        background: '#1f2937',
                        color: '#ffffff',
                        padding: '1rem 1.5rem',
                        borderRadius: '8px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
                        zIndex: 1500,
                        fontSize: '0.9rem',
                        maxWidth: '300px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>🔧</span>
                            <strong>Demo Mode Active</strong>
                        </div>
                        <p style={{ margin: 0, opacity: 0.9, fontSize: '0.8rem' }}>
                            API not available. Showing sample data for demonstration.
                        </p>
                    </div>
                )
            }
        </Layout>
    );
};

export default AdminTeacherAttendanceManagement;